/**
 * Shared formatting utilities
 */

import { HOUR, MINUTE, DAY, isNeverExpires } from '../constants/expiry'

/**
 * Format bytes to human readable string
 * @param bytes - Number of bytes to format
 * @returns Formatted string like "1.5 KB" or "2.3 MB"
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(1)} KB`
  const mb = kb / 1024
  if (mb < 1024) return `${mb.toFixed(1)} MB`
  const gb = mb / 1024
  return `${gb.toFixed(1)} GB`
}

/**
 * Format a number with thousands separators
 * @param num - Number to format
 * @returns Formatted string like "1,234,567"
 */
export function formatNumber(num: number): string {
  return num.toLocaleString()
}

/**
 * Format a percentage
 * @param value - Current value
 * @param max - Maximum value
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string like "75.5%"
 */
export function formatPercentage(value: number, max: number, decimals = 1): string {
  if (max === 0) return '0%'
  const percentage = (value / max) * 100
  return `${percentage.toFixed(decimals)}%`
}

/**
 * Format a timestamp as a locale date string
 * When isExpiry is true, "never expires" timestamps return "Never"
 */
export function formatDate(timestamp: number, isExpiry = false): string {
  if (isExpiry && isNeverExpires(timestamp)) {
    return 'Never'
  }
  return new Date(timestamp).toLocaleString()
}

/**
 * Format an expiry timestamp for display. Returns "Never" for non-expiring shares.
 */
export function formatExpiry(timestamp: number): string {
  return formatDate(timestamp, true)
}

/**
 * Format time remaining until a deadline as "Xh Ym" or "Xm"
 */
export function formatTimeRemaining(expiresAt: number): string {
  const ms = expiresAt - Date.now()
  const hours = Math.floor(ms / HOUR)
  const minutes = Math.floor((ms % HOUR) / MINUTE)
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
}

/**
 * Format a timestamp as a human-readable relative time (e.g. "3 days", "2 hours").
 * Returns "Never" for non-expiring timestamps, "Expired" for past timestamps.
 */
export function formatRelativeTime(timestamp: number): string {
  if (isNeverExpires(timestamp)) return 'Never'

  const diff = timestamp - Date.now()
  if (diff < 0) return 'Expired'

  const hours = Math.floor(diff / HOUR)
  const days = Math.floor(hours / 24)

  if (days > 0) return `${days} day${days > 1 ? 's' : ''}`
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`
  return 'Less than an hour'
}

/**
 * Format account expiration in days
 * Returns null if the account never expires
 */
export function formatAccountExpiration(expiresAt: number | null): string | null {
  if (expiresAt === null) return null
  if (isNeverExpires(expiresAt)) return null

  const ms = expiresAt - Date.now()
  if (ms <= 0) return 'expired'

  const days = Math.ceil(ms / DAY)
  return `${days} day${days !== 1 ? 's' : ''}`
}
